// packages/core/src/learning/LearningTypes.ts
// ─────────────────────────────────────────────────────────────────────────
// Single source of truth for all Learn system types.
// Generated lesson batches must conform to the Lesson interface below.
//
// V2.4 additions (Schools-as-Worldviews):
//   - LessonKind  → 'map' | 'school-core' | 'school-extra'
//   - ModuleSurface → 'map' | 'school'
//   - Lesson.lessonKind?, Lesson.concepts?, Lesson.figures?
//   - LearningModule.surface?, LearningModule.homeSchool?
//   - ListedSchool (the browsable subset, excludes the catch-all)
// All additions are optional → existing modules continue to behave as Map
// content with no changes.
// ─────────────────────────────────────────────────────────────────────────

import { TIERS, PILLARS, SCHOOLS } from './CurriculumMeta';
import type { FigureId } from './CurriculumMeta';

// ── Taxonomy unions (derived from CurriculumMeta) ────────────────────────
export type Tier = (typeof TIERS)[number];
export type Pillar = (typeof PILLARS)[number];
export type SchoolOfThought = (typeof SCHOOLS)[number];

/** The browsable subset of schools. Excludes `personal-finance-basics`,
 *  which is a catch-all tag and not surfaced as a destination. Defined
 *  here (once) so SchoolMeta and SchoolExhibitMeta can both import it
 *  without duplicate-export collisions. */
export type ListedSchool = Exclude<SchoolOfThought, 'personal-finance-basics'>;

// ── Surface / kind discriminators (V2.4) ─────────────────────────────────
/** Where a lesson plays its role. Default behaviour when undefined:
 *  - module.surface === 'map'    → lesson treated as 'map'
 *  - module.surface === 'school' → lesson treated as 'school-core' */
export type LessonKind = 'map' | 'school-core' | 'school-extra';

/** Where a module lives. Undefined defaults to 'map' for backward compat. */
export type ModuleSurface = 'map' | 'school';

// ── Blocks (lesson body content) ─────────────────────────────────────────
export type Block =
	| { type: 'paragraph'; text: string }
	| { type: 'bullet_list'; items: string[] }
	| { type: 'callout'; icon?: string; text: string }
	| { type: 'example'; label?: string; text: string }
	| { type: 'key_takeaway'; text: string };

// ── Quiz / assessment questions (3 types — used in ModuleAssessment) ─────
export interface MCQuestion {
	type: 'multiple_choice';
	id: string;
	question: string;
	options: string[];
	correctIndex: number;
	explanation: string;
}

export interface TFQuestion {
	type: 'true_false';
	id: string;
	question: string;
	correct: boolean;
	explanation: string;
}

export interface ScenarioQuestion {
	type: 'scenario';
	id: string;
	scenario: string;
	question: string;
	options: string[];
	correctIndex: number;
	explanation: string;
}

export type Question = MCQuestion | TFQuestion | ScenarioQuestion;

// ── Flashcards ────────────────────────────────────────────────────────────
// Every NORMAL lesson ships exactly 3 core flashcards — the lesson's "memory
// anchors" — one of each role:
//   • main_idea → the core concept to remember
//   • watch_out → the common mistake / trap / misconception
//   • use_it    → the real-life application or judgment test
//
// `role` is optional ONLY for backward-compat with legacy batches generated
// before V2 of the flashcard spec. New content MUST set it, and the generation
// prompt + Zod schema enforce one-card-per-role. The mastery policy layer
// (flashcardMasteryPolicy.ts) infers a fallback role by position for any
// legacy card that lacks one, so nothing breaks at runtime.
export type FlashcardRole = 'main_idea' | 'watch_out' | 'use_it';

/** The three roles in canonical display order. */
export const FLASHCARD_ROLES: readonly FlashcardRole[] = [
	'main_idea',
	'watch_out',
	'use_it',
] as const;

/** User-facing labels for each role. */
export const FLASHCARD_ROLE_LABELS: Record<FlashcardRole, string> = {
	main_idea: 'Main idea',
	watch_out: 'Watch out',
	use_it: 'Use it',
};

export interface Flashcard {
	id: string;
	front: string;
	back: string;
	/** Memory-anchor role. Optional for legacy data; see note above. */
	role?: FlashcardRole;
}

// ── Apply Actions (locked discriminated union) ────────────────────────────
export type ScenarioKind =
	| 'aggressive-savings'
	| 'side-hustle'
	| 'second-job'
	| 'expense-slash'
	| 'coast-fi'
	| 'custom';

export type ApplyAction =
	| { type: 'openDashboard'; label: string }
	| {
			type: 'openSimulator';
			label: string;
			/** Selects which wizard card to focus (no values pre-filled). */
			scenarioType?: ScenarioKind;
			/** Fully hydrates a scenario: the simulator creates this exact
			 *  scenario on entry, scrolls to it, and plays the create
			 *  animation. `kind` is mapped to the store's ScenarioType via
			 *  kindToScenarioType(). `data` is the wizard's field map
			 *  (e.g. { monthlyExpenseCut: '500' }). Passed via router
			 *  navigation STATE, never the URL. */
			simulatorScenario?: {
				kind: ScenarioKind;
				name?: string;
				data: Record<string, string>;
			};
	  }
	| { type: 'calculateFiNumber'; label: string }
	| { type: 'analyzeSavingsRate'; label: string }
	| { type: 'calculateRunway'; label: string }
	| { type: 'simulateDebtPayoff'; label: string }
	| { type: 'simulateCarCost'; label: string }
	| { type: 'calculateEmergencyFund'; label: string }
	| { type: 'compareRentVsBuy'; label: string }
	| { type: 'buildFreedomPlan'; label: string }
	| { type: 'reviewCreditUsage'; label: string }
	| { type: 'createBudgetPlan'; label: string }
	| { type: 'runFreedomCost'; label: string; amount?: number }
	// ── Impact Lab links (Nisk Core Path rebuild) ───────────────────────
	/** Deep-link to a specific Impact Lab scenario by its stable slug id. */
	| { type: 'openImpactScenario'; label: string; scenarioId: string }
	/** Open the Impact Lab, optionally pre-filtered to a category. */
	| { type: 'openImpactLab'; label: string; categoryId?: string }
	| { type: 'noToolAction'; label: string; prompt: string };

export const APPLY_ACTION_ROUTES: Record<ApplyAction['type'], string | null> = {
	openDashboard: '/dashboard',
	openSimulator: '/simulate/plan',
	calculateFiNumber: null,
	analyzeSavingsRate: '/dashboard',
	calculateRunway: '/dashboard',
	simulateDebtPayoff: null,
	simulateCarCost: null,
	calculateEmergencyFund: null,
	compareRentVsBuy: null,
	buildFreedomPlan: '/simulate/plan',
	reviewCreditUsage: null,
	createBudgetPlan: null,
	runFreedomCost: null,
	openImpactScenario: '/impact-lab', // deep-links to /impact-lab/:scenarioId at call site
	openImpactLab: '/impact-lab',
	noToolAction: null,
};

// ── Lesson ────────────────────────────────────────────────────────────────
export interface Lesson {
	id: string;
	title: string;
	description: string;
	pillar: Pillar;
	schoolsOfThought: SchoolOfThought[];
	/**
	 * @deprecated Not used for display. Read time is now DERIVED from `blocks`
	 * via estimateReadMinutes() (see learning/readTime.ts). Listen time comes
	 * from the manifest's measured durationSeconds. These authored fields remain
	 * on the type for content-schema compatibility but should not be rendered.
	 */
	estimatedReadMinutes: number;
	/** @deprecated Not used for display. See estimatedReadMinutes note above. */
	estimatedListenMinutes: number;
	difficulty: 1 | 2 | 3 | 4 | 5;
	tags: string[];
	prerequisites: string[];
	relatedLessons: string[];
	blocks: Block[];
	audioScript: string;
	audioUrl?: string;
	flashcards: Flashcard[];
	applyAction?: ApplyAction;
	revision: number;

	// ── V2.4 additions (all optional) ────────────────────────────────────
	/** Where this lesson plays its role. See LessonKind. */
	lessonKind?: LessonKind;
	/** Atomic ideas this lesson teaches. Free-string for now; the dev
	 *  concept registry surfaces near-duplicates as console warnings.
	 *  Vocabulary will tighten later (see CONCEPTS_TODO.md). */
	concepts?: string[];
	/** Finance figures this lesson teaches the ideas of (e.g. ['john-bogle']).
	 *  Optional — only set when a lesson is genuinely about a figure. */
	figures?: FigureId[];
}

// ── Module Assessment (Nisk Core Path — single checkpoint + final) ───────
// Reshaped for the lean 5-lesson core modules:
//   checkpointQuiz → 6–8 questions, taken after lesson 3
//   finalTest      → 10–12 questions, taken after lesson 5
// (Legacy two-checkpoint modules are dropped from the active path.)
export type AssessmentId = 'cq' | 'final';

export interface ModuleAssessment {
	moduleId: string;
	checkpointQuiz: Question[]; // 6–8 questions, covers lessons 1–3
	finalTest: Question[]; // 10–12 questions, covers all 5 lessons
}

// ── Learning Module ───────────────────────────────────────────────────────
export interface LearningModule {
	id: string;
	title: string;
	description: string;
	emoji: string;
	accentColor: string;
	tier: Tier;
	pillar: Pillar;
	order: number;
	lessons: Lesson[];
	assessment?: ModuleAssessment; // populated when batch is generated

	// ── V2.4 additions (all optional) ────────────────────────────────────
	/** Where this module lives. Undefined = 'map' (backward compat). School
	 *  modules also set `homeSchool`. School modules do NOT carry assessments. */
	surface?: ModuleSurface;
	/** For surface === 'school': which school this module belongs to. */
	homeSchool?: SchoolOfThought;
}

// ── Per-user progress ────────────────────────────────────────────────────
// NOTE on the two-tier flashcard model (V2 flashcard spec §5):
//   • `flashcards_checkpoint` → SHORT-TERM proof. The user reviewed the
//     lesson's anchors and got ≥2 of 3 active cards "solid" (latest rating
//     Remembered/Easy). This is what gates LESSON PROGRESS — no time-gate.
//   • `flashcards_mastered`   → LONG-TERM retention. The engine's strict
//     isMastered bar (≥4 reps, ≥21-day interval, ≤1 lapse). Used for badges,
//     review-health, and the optional "retained" reward — NOT lesson progress.
export type LessonMasteryState =
	| 'not_started'
	| 'started'
	| 'read'
	| 'listened'
	| 'quiz_passed'
	| 'flashcards_checkpoint'
	| 'flashcards_mastered'
	| 'applied'
	| 'mastered';

export interface LessonProgress {
	lessonId: string;
	state: LessonMasteryState;
	revisionAtCompletion?: number;
	readAt?: string;
	listenedAt?: string;
	quizScore?: number;
	quizPassedAt?: string;
	flashcardsCheckpointAt?: string;
	flashcardsMasteredAt?: string;
	appliedAt?: string;
	masteredAt?: string;
}

// ── Assessment constants (used by Quiz / Module pages) ───────────────────
export const ASSESSMENT_IDS = ['cq', 'final'] as const;

export const ASSESSMENT_LABELS: Record<AssessmentId, string> = {
	cq: 'Checkpoint Quiz',
	final: 'Final Exam',
};

export const ASSESSMENT_SHORT_LABELS: Record<AssessmentId, string> = {
	cq: 'Checkpoint',
	final: 'Final Exam',
};

export const ASSESSMENT_EMOJIS: Record<AssessmentId, string> = {
	cq: '📝',
	final: '🏆',
};

/** Minimum proportion correct to pass each assessment. */
export const ASSESSMENT_PASS_THRESHOLDS: Record<AssessmentId, number> = {
	cq: 0.7,
	final: 0.8,
};
